import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Clock, ChevronRight, ArrowRight } from 'lucide-react';
import { blogData } from '../data/blogData';
import { SEOHead } from '../seo';
import { TopBannerAd } from './TopBannerAd';

export const Blog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      <SEOHead
        title="Toolmetrico Blog - Guides & Tutorials"
        description="Expert guides on image compression, PDF merging, QR code marketing, and web development. Read our latest articles to master digital utilities."
        keywords="toolmetrico blog, image compression guide, qr code marketing, web developer tools"
        path="/blog"
      />

      {/* Hero Section */}
      <section style={{ textAlign: 'center', margin: '40px 0 60px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--bg-secondary)', padding: '8px 16px', borderRadius: '99px', color: 'var(--primary)', fontWeight: 700, fontSize: '0.85rem', marginBottom: '20px' }}>
          <BookOpen size={16} />
          <span>Editorial & Guides</span>
        </div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--text-primary)', marginBottom: '16px', letterSpacing: '-0.03em' }}>
          Master Your Digital Workflow
        </h1>
        <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
          Deep-dive tutorials, industry best practices, and expert insights on optimizing assets and leveraging online tools.
        </p>
      </section>

      {/* Blog Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: '32px',
        padding: '0 0 60px 0'
      }}>
        {blogData.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            style={{
              textDecoration: 'none',
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid var(--border-color)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 'var(--shadow-sm)',
              transition: 'transform 0.3s, box-shadow 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 20px 40px -15px rgba(5, 117, 251, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            {/* Image Container */}
            <div style={{ width: '100%', height: '220px', overflow: 'hidden', position: 'relative', background: 'var(--bg-secondary)' }}>
              <img
                src={post.image}
                alt={post.title}
                onError={(e) => {
                  if (!e.currentTarget.dataset.retried) {
                    e.currentTarget.dataset.retried = "true";
                    e.currentTarget.src = `https://picsum.photos/seed/${post.slug}/800/500`;
                  } else {
                    e.currentTarget.src = "/logo512.png";
                  }
                }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
              />
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                padding: '6px 14px',
                borderRadius: '99px',
                fontSize: '0.75rem',
                fontWeight: 800,
                color: 'var(--text-primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {post.category}
              </div>
            </div>

            {/* Content Container */}
            <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600, marginBottom: '12px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Calendar size={14} /> {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={14} /> {post.readTime}
                </span>
              </div>

              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 800,
                color: 'var(--text-primary)',
                lineHeight: 1.4,
                margin: '0 0 12px 0'
              }}>
                {post.title}
              </h2>

              <p style={{
                fontSize: '0.9rem',
                color: 'var(--text-secondary)',
                lineHeight: 1.6,
                margin: '0 0 24px 0',
                flexGrow: 1
              }}>
                {post.excerpt}
              </p>

              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--primary)',
                fontWeight: 700,
                fontSize: '0.9rem'
              }}>
                Read Article <ArrowRight size={16} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Blog;
